import React from "react";
import {
  Box,
  Text,
  SimpleGrid,
  Container,
  Heading,
  VStack,
} from "@chakra-ui/react";
import {
  FaWhatsapp,
  FaLaptopCode,
  FaBrain,
} from "react-icons/fa";
import { TbMessageChatbot } from "react-icons/tb";
import { AiTwotoneApi } from "react-icons/ai";
import { MdSystemSecurityUpdateGood } from "react-icons/md";
import { useNavigate } from "react-router-dom";

// ServiceCard with dynamic icon color scheme
const ServiceCard = ({
  icon: Icon,
  title,
  description,
  onCardClick,
  iconColorScheme,
}) => (
  <Box
    bg="white"
    borderRadius="lg"
    boxShadow="md"
    p={8}
    textAlign="left"
    transition="all 0.3s ease-in-out"
    _hover={{
      transform: "translateY(-5px)",
      boxShadow: "xl",
    }}
    border="1px solid"
    borderColor="gray.200"
    cursor="pointer"
    onClick={onCardClick}
  >
    <VStack spacing={4} align="start">
      <Box
        p={3}
        borderRadius="full"
        bg={iconColorScheme?.bg || "gray.100"}
        color={iconColorScheme?.color || "gray.600"}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon size={30} />
      </Box>
      <Heading size="md" color="gray.800" fontWeight="semibold">
        {title}
      </Heading>
      <Text color="gray.600" fontSize="sm" lineHeight="1.6">
        {description}
      </Text>
    </VStack>
  </Box>
);

const CardFirst = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: FaLaptopCode,
      title: "Website Development",
      description: "Create stunning, responsive websites that drive business growth",
      technology: "web-development",
    },
    {
      icon: MdSystemSecurityUpdateGood,
      title: "App Development",
      description: "Native and cross-platform mobile applications for iOS and Android",
      technology: "app-development",
    },
    {
      icon: AiTwotoneApi,
      title: "API Development",
      description: "Robust and scalable APIs to power your applications",
      technology: "api-development",
    },
    {
      icon: TbMessageChatbot,
      title: "Chatbot Development",
      description: "Intelligent chatbots to enhance customer engagement",
      technology: "chatbot-development",
    },
    {
      icon: FaWhatsapp,
      title: "WhatsApp Bot Development",
      description: "Automate customer service with WhatsApp Business API",
      technology: "whatsapp-bot-development",
    },
    {
      icon: FaBrain,
      title: "Generative AI Development Services",
      description: "Adopt next-level innovation to increase business capabilities, save costs, and enhance productivity with intelligent AI solutions.",
      technology: "ai-development",
    },
  ];

  const colorMap = {
    // Web Technologies
    ".Net": { bg: "purple.50", color: "purple.500" },
    "Python": { bg: "blue.50", color: "blue.500" },
    "Ruby On Rails": { bg: "red.50", color: "red.500" },
    "GraphQL": { bg: "pink.50", color: "pink.500" },
    "PHP": { bg: "gray.50", color: "gray.500" },

    // PHP Technologies
    "Laravel": { bg: "red.50", color: "red.500" },
    "CodeIgniter": { bg: "orange.50", color: "orange.500" },
    "Zend": { bg: "blue.50", color: "blue.500" },

    // Mobile Technologies
    "React Native": { bg: "cyan.50", color: "cyan.500" },
    "Flutter": { bg: "blue.50", color: "blue.500" },
    "iOS": { bg: "gray.50", color: "gray.500" },
    "Android": { bg: "green.50", color: "green.500" },

    // Database Technologies
    "MongoDB": { bg: "green.50", color: "green.500" },
    "MySQL": { bg: "blue.50", color: "blue.500" },
    "PostgreSQL": { bg: "blue.50", color: "blue.500" },

    // JavaScript Technologies
    "Angular": { bg: "red.50", color: "red.500" },
    "Node.js": { bg: "green.50", color: "green.500" },
    "React.js": { bg: "blue.50", color: "blue.500" },
    "Vue.js": { bg: "green.50", color: "green.500" },

    // Full-stack Technologies
    "MEAN Stack": { bg: "green.50", color: "green.500" },
    "MERN Stack": { bg: "blue.50", color: "blue.500" },
    "Blockchain": { bg: "purple.50", color: "purple.500" },
    "DevOps": { bg: "orange.50", color: "orange.500" },

    // AI/ML Technologies
    "AI": { bg: "purple.50", color: "purple.500" },
    "Machine Learning": { bg: "blue.50", color: "blue.500" },
    "ChatBot": { bg: "green.50", color: "green.500" },
    "IOT": { bg: "orange.50", color: "orange.500" },
    "Deep Learning": { bg: "purple.50", color: "purple.500" },

    // Service Cards (keeping existing mappings)
    "Website Development": { bg: "blue.50", color: "blue.500" },
    "App Development": { bg: "purple.50", color: "purple.500" },
    "API Development": { bg: "orange.50", color: "orange.500" },
    "Chatbot Development": { bg: "teal.50", color: "teal.500" },
    "WhatsApp Bot Development": { bg: "green.50", color: "green.500" },
    "Generative AI Development Services": { bg: "pink.50", color: "pink.500" },
  };

  const handleCardClick = (technology) => {
    // Store the selected technology in localStorage
    localStorage.setItem('selectedTechnology', technology);
    // Navigate to the Services Offered page
    navigate('/Services offered');
  };

  return (
    <Container maxW="container.xl" py={20}>
      <VStack spacing={12}>
        <Heading
          size="xl"
          textAlign="center"
          bgGradient="linear(to-r, blue.500, teal.500)"
          bgClip="text"
        >
          Our Services
        </Heading>
 
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={8}
          w="100%"
        >
          {services.map((service, index) => (
            <ServiceCard 
              key={index} 
              {...service} 
              onCardClick={() => handleCardClick(service.technology)}
              iconColorScheme={colorMap[service.title]}
            />
          ))}
        </SimpleGrid>
      </VStack>
    </Container>
  );
};

export default CardFirst;
